import {
  InquiryStageEnum,
  InquiryTagEnum,
  BookingStageEnum,
  EventStageEnum,
  QuoteStatusEnum,
  PaymentStatusEnum,
  InvoiceStatusEnum,
  PaymentScheduleStatusEnum,
  ItemUnitEnum,
  ParentTypeEnum,
  DiscountTypeEnum,
  UserSchedulingStatusEnum,
  TaskStatusEnum,
  JobPhaseEnum,
  JobStatusEnum,
  OrganizationRoleEnum,
  OrganizationFeatureEnum,
  PermissionEnum,
  InquiryRejectReasonEnum,
} from '../gen/cortado/v1/settings_pb'
import { EmailThreadStatus } from '../gen/cortado/v1/emails_pb'

/**
 * Map of enum types to their proto prefixes
 * This allows us to explicitly strip the correct prefix for each enum type
 */
const ENUM_TYPE_TO_PREFIX = new Map<any, string>([
  [InquiryStageEnum, 'INQUIRY_STAGE_'],
  [InquiryTagEnum, 'INQUIRY_TAG_'],
  [BookingStageEnum, 'BOOKING_STAGE_'],
  [EventStageEnum, 'EVENT_STAGE_'],
  [QuoteStatusEnum, 'QUOTE_STATUS_'],
  [PaymentStatusEnum, 'PAYMENT_STATUS_'],
  [InvoiceStatusEnum, 'INVOICE_STATUS_'],
  [PaymentScheduleStatusEnum, 'PAYMENT_SCHEDULE_STATUS_'],
  [ItemUnitEnum, 'ITEM_UNIT_'],
  [ParentTypeEnum, 'PARENT_TYPE_'],
  [DiscountTypeEnum, 'DISCOUNT_TYPE_'],
  [UserSchedulingStatusEnum, 'USER_SCHEDULING_STATUS_'],
  [TaskStatusEnum, 'TASK_STATUS_'],
  [JobPhaseEnum, 'JOB_PHASE_'],
  [JobStatusEnum, 'JOB_STATUS_'],
  [OrganizationRoleEnum, 'ORGANIZATION_ROLE_'],
  [OrganizationFeatureEnum, 'ORGANIZATION_FEATURE_'],
  [PermissionEnum, 'PERMISSION_'],
  [InquiryRejectReasonEnum, 'INQUIRY_REJECT_REASON_'],
  [EmailThreadStatus, 'EMAIL_THREAD_STATUS_'],
])

/**
 * Convert a proto enum value to a human-readable display string
 * Automatically strips the enum prefix based on the enum type
 * 
 * @param value - The numeric enum value
 * @param enumType - The enum type object (e.g., InquiryStageEnum, TaskStatusEnum)
 * @returns Formatted human-readable string
 * 
 * @example
 * formatEnumName(2, InquiryStageEnum)
 * // InquiryStageEnum[2] = "INQUIRY_STAGE_IN_PROGRESS" -> "In Progress"
 * 
 * @example
 * formatEnumName(status, TaskStatusEnum)
 * // "TASK_STATUS_NOT_STARTED" -> "Not Started"
 */
export function formatEnumName(value: number, enumType: any): string {
  // Get the string name from the enum value
  const enumName = enumType[value]
  
  if (!enumName) {
    return 'Unknown'
  }
  
  // Get the prefix for this enum type
  const prefix = ENUM_TYPE_TO_PREFIX.get(enumType)
  
  // Strip the prefix if found
  let nameToFormat = enumName
  if (prefix && enumName.startsWith(prefix)) {
    nameToFormat = enumName.slice(prefix.length)
  }
  
  // Format the remaining string
  return nameToFormat
    .split('_')
    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Badge color variants for consistent styling across the app
 */
export type BadgeVariant = 
  | 'default' 
  | 'secondary' 
  | 'success' 
  | 'warning' 
  | 'danger' 
  | 'info' 
  | 'purple'
  | 'pink'
  | 'orange'

/**
 * Get the appropriate badge color variant for an inquiry stage
 */
export function getInquiryStageColor(stage: InquiryStageEnum): BadgeVariant {
  const stageColors: Record<number, BadgeVariant> = {
    0: 'default', // UNKNOWN
    1: 'info', // NEW
    2: 'warning', // IN_PROGRESS
    3: 'purple', // QUOTE_SENT
    4: 'success', // ACCEPTED
    5: 'danger', // REJECTED
    6: 'secondary', // STALE
    7: 'orange', // REFERRED
  }
  return stageColors[stage] || 'default'
}

/**
 * A lead is "booked" once a quote acceptance has created its booking (and
 * invoice). Bookings are irreversible and one-per-inquiry, so this is
 * effectively a terminal state: no further quote on this lead can ever be
 * accepted. Proto has no distinct "booked" stage — `INQUIRY_STAGE_ACCEPTED`
 * covers both "accepted, no booking yet" and "accepted, booking created" —
 * so this derives the distinction from `bookingId`. This is the single
 * source of truth for that derivation; don't re-check `bookingId` ad hoc.
 */
export function isBooked(inquiry: { bookingId?: string }): boolean {
  return Boolean(inquiry.bookingId)
}

export interface InquiryStageBadgeDescriptor {
  label: string
  variant: BadgeVariant
}

/**
 * Badge descriptor for an inquiry's stage, accounting for the "booked" case.
 * A booked lead renders a distinct "Booked" badge instead of the plain
 * "Accepted" stage badge, so staff can tell "won and converted to an event"
 * apart from "accepted, awaiting event creation" at a glance. Every surface
 * that renders an inquiry's stage badge should go through this helper
 * instead of calling getInquiryStageColor directly.
 */
export function getInquiryStageBadge(
  inquiry: { stage: InquiryStageEnum; bookingId?: string }
): InquiryStageBadgeDescriptor {
  if (isBooked(inquiry)) {
    return { label: 'Booked', variant: 'purple' }
  }
  return {
    label: formatEnumName(inquiry.stage, InquiryStageEnum),
    variant: getInquiryStageColor(inquiry.stage),
  }
}

/**
 * Get the appropriate badge color variant for an inquiry tag
 */
export function getInquiryTagColor(tag: InquiryTagEnum): BadgeVariant {
  const tagColors: Record<number, BadgeVariant> = {
    0: 'default', // UNKNOWN
    1: 'info', // FOLLOW_UP (blue)
    2: 'danger', // URGENT (red)
    3: 'warning', // HOT_LEAD (yellow)
    4: 'purple', // VIP (purple)
    5: 'secondary', // WAITING (gray)
    6: 'danger', // REPLY_NEEDED (red)
  }
  return tagColors[tag] || 'default'
}

/**
 * Get the short label for an inquiry tag (for compact display)
 */
export function getInquiryTagShortLabel(tag: InquiryTagEnum): string {
  const shortLabels: Record<number, string> = {
    0: '?', // UNKNOWN
    1: 'F', // FOLLOW_UP
    2: 'U', // URGENT
    3: 'H', // HOT_LEAD
    4: 'V', // VIP
    5: 'W', // WAITING
    6: 'R', // REPLY_NEEDED
  }
  return shortLabels[tag] || '?'
}

/**
 * Get the full label for an inquiry tag
 */
export function getInquiryTagLabel(tag: InquiryTagEnum): string {
  const labels: Record<number, string> = {
    0: 'Unknown', // UNKNOWN
    1: 'Follow Up', // FOLLOW_UP
    2: 'Urgent', // URGENT
    3: 'Hot Lead', // HOT_LEAD
    4: 'VIP', // VIP
    5: 'Waiting', // WAITING
    6: 'Reply needed', // REPLY_NEEDED
  }
  return labels[tag] || 'Unknown'
}

/**
 * Get all available inquiry tags (excluding UNKNOWN)
 */
export function getAllInquiryTags(): InquiryTagEnum[] {
  return [
    InquiryTagEnum.INQUIRY_TAG_FOLLOW_UP,
    InquiryTagEnum.INQUIRY_TAG_URGENT,
    InquiryTagEnum.INQUIRY_TAG_HOT_LEAD,
    InquiryTagEnum.INQUIRY_TAG_VIP,
    InquiryTagEnum.INQUIRY_TAG_WAITING,
    InquiryTagEnum.INQUIRY_TAG_REPLY_NEEDED,
  ]
}

/**
 * Get the appropriate badge color variant for a booking stage
 */
export function getBookingStageColor(stage: BookingStageEnum): BadgeVariant {
  const stageColors: Record<number, BadgeVariant> = {
    0: 'default', // UNKNOWN
    1: 'info', // UPCOMING
    2: 'success', // COMPLETE
    3: 'danger', // CANCELLED
  }
  return stageColors[stage] || 'default'
}

/**
 * Get the appropriate badge color variant for an event stage
 */
export function getEventStageColor(stage: EventStageEnum): BadgeVariant {
  const stageColors: Record<number, BadgeVariant> = {
    0: 'default', // UNKNOWN
    1: 'info', // UPCOMING
    2: 'success', // COMPLETE
    3: 'danger', // CANCELLED
    4: 'warning', // RESCHEDULED
    5: 'orange', // POSTPONED
  }
  return stageColors[stage] || 'default'
}

/**
 * Get the appropriate badge color variant for a quote status
 */
export function getQuoteStatusColor(status: QuoteStatusEnum): BadgeVariant {
  const statusColors: Record<number, BadgeVariant> = {
    0: 'default', // UNKNOWN
    1: 'secondary', // DRAFT
    2: 'info', // SENT
    3: 'warning', // VIEWED
    4: 'purple', // REVISED
    5: 'success', // ACCEPTED
    6: 'danger', // REJECTED
    7: 'danger', // EXPIRED
    8: 'secondary', // CANCELLED
  }
  return statusColors[status] || 'default'
}

/**
 * Frontend display labels for quote statuses.
 * Keep the proto enum unchanged while using customer-facing product language.
 */
export function getQuoteStatusLabel(status: QuoteStatusEnum): string {
  const statusLabels: Partial<Record<QuoteStatusEnum, string>> = {
    [QuoteStatusEnum.QUOTE_STATUS_SENT]: 'Live in portal',
  }

  return statusLabels[status] || formatEnumName(status, QuoteStatusEnum)
}

/**
 * Get the appropriate badge color variant for a payment status
 */
export function getPaymentStatusColor(status: PaymentStatusEnum): BadgeVariant {
  const statusColors: Record<number, BadgeVariant> = {
    0: 'default', // UNKNOWN
    1: 'warning', // UNPAID
    2: 'success', // SUCCESS
    3: 'danger', // FAILED
    4: 'purple', // REFUNDED
    5: 'orange', // DISPUTED
  }
  return statusColors[status] || 'default'
}

/**
 * Get the appropriate badge color variant for an invoice status
 */
export function getInvoiceStatusColor(status: InvoiceStatusEnum): BadgeVariant {
  const statusColors: Record<number, BadgeVariant> = {
    0: 'default', // UNKNOWN
    1: 'secondary', // DRAFT
    2: 'info', // SENT
    3: 'info', // VIEWED
    4: 'warning', // PAYMENT_PENDING
    5: 'warning', // PARTIALLY_PAID
    6: 'success', // PAID
    7: 'danger', // OVERDUE
    8: 'secondary', // CANCELLED
    9: 'warning', // OVERPAID
  }
  return statusColors[status] || 'default'
}

/**
 * Frontend display labels for invoice statuses.
 * Keep the proto enum unchanged while using client-visibility product language.
 */
export function getInvoiceStatusLabel(status: InvoiceStatusEnum): string {
  const statusLabels: Partial<Record<InvoiceStatusEnum, string>> = {
    [InvoiceStatusEnum.INVOICE_STATUS_SENT]: 'Live in portal',
  }

  return statusLabels[status] || formatEnumName(status, InvoiceStatusEnum)
}

/**
 * Get the appropriate badge color variant for an item unit
 */
export function getItemUnitColor(unit: ItemUnitEnum): BadgeVariant {
  const unitColors: Record<number, BadgeVariant> = {
    0: 'default', // UNKNOWN
    1: 'info', // PER_EVENT
    2: 'warning', // PER_HOUR
    3: 'success', // PER_DAY
    4: 'secondary', // PER_UNIT
  }
  return unitColors[unit] || 'default'
}

/**
 * Get the appropriate badge color variant for a parent type
 */
export function getParentTypeColor(type: ParentTypeEnum): BadgeVariant {
  // Parent types are more neutral, use limited color palette
  const typeColors: Record<number, BadgeVariant> = {
    0: 'default', // UNKNOWN
    1: 'purple', // ORGANIZATION
    2: 'info', // INQUIRY
    3: 'success', // BOOKING
    4: 'warning', // EVENT
    5: 'secondary', // CONTACT
    6: 'info', // ITEM
    7: 'warning', // QUOTE
    8: 'secondary', // SALES_TAX
    9: 'success', // INVOICE
    10: 'secondary', // NOTE
    11: 'warning', // TASK
    12: 'info', // EMAIL
    13: 'purple', // ORGANIZATION_ROLE
    14: 'secondary', // EMAIL_TEMPLATE
    15: 'info', // ACTOR
    16: 'warning', // DISCOUNT
  }
  return typeColors[type] || 'default'
}

/**
 * Get the appropriate badge color variant for a discount type
 */
export function getDiscountTypeColor(type: DiscountTypeEnum): BadgeVariant {
  const typeColors: Record<number, BadgeVariant> = {
    0: 'default', // UNKNOWN
    1: 'success', // PERCENTAGE
    2: 'info', // FIXED
  }
  return typeColors[type] || 'default'
}

/**
 * Get the appropriate badge color variant for a user scheduling status
 */
export function getUserSchedulingStatusColor(status: UserSchedulingStatusEnum): BadgeVariant {
  const statusColors: Record<number, BadgeVariant> = {
    0: 'default', // UNKNOWN
    1: 'warning', // PENDING
    2: 'success', // ACCEPTED
    3: 'danger', // REJECTED
  }
  return statusColors[status] || 'default'
}

/**
 * Get the appropriate badge color variant for a task status
 */
export function getTaskStatusColor(status: TaskStatusEnum): BadgeVariant {
  const statusColors: Record<number, BadgeVariant> = {
    0: 'default', // UNKNOWN
    1: 'secondary', // NOT_STARTED
    2: 'warning', // IN_PROGRESS
    3: 'success', // COMPLETED
  }
  return statusColors[status] || 'default'
}

/**
 * Get the appropriate badge color variant for an organization role
 */
export function getOrganizationRoleColor(role: OrganizationRoleEnum): BadgeVariant {
  const roleColors: Record<number, BadgeVariant> = {
    0: 'default', // UNKNOWN
    1: 'purple', // OWNER
    2: 'warning', // ADMIN
    3: 'info', // BARISTA
  }
  return roleColors[role] || 'default'
}

/**
 * Get the appropriate badge color variant for an organization feature
 */
export function getOrganizationFeatureColor(feature: OrganizationFeatureEnum): BadgeVariant {
  const featureColors: Record<number, BadgeVariant> = {
    0: 'default', // UNKNOWN
    1: 'info', // EMAIL
    2: 'purple', // AI_TEMPLATES
    3: 'success', // AI_CHAT
  }
  return featureColors[feature] || 'default'
}

/**
 * Get the appropriate badge color variant for a permission
 */
export function getPermissionColor(permission: PermissionEnum): BadgeVariant {
  const permissionColors: Record<number, BadgeVariant> = {
    0: 'default', // UNKNOWN
    1: 'danger', // SYSTEM
    2: 'warning', // ADMIN
  }
  return permissionColors[permission] || 'default'
}

/**
 * Get the appropriate badge color variant for an email thread status
 */
export function getEmailThreadStatusColor(status: EmailThreadStatus): BadgeVariant {
  const statusColors: Record<number, BadgeVariant> = {
    0: 'default', // UNSPECIFIED
    1: 'info', // OPEN
    2: 'secondary', // CLOSED
    3: 'purple', // SNOOZED
    4: 'warning', // NEEDS_REVIEW
  }
  return statusColors[status] || 'default'
}

/**
 * Convert ItemUnitEnum to backend string format
 * Example: ItemUnitEnum.ITEM_UNIT_PER_HOUR (2) → "PerHour"
 *
 * @param enumValue - The numeric ItemUnitEnum value
 * @returns Backend-formatted string (e.g., "PerHour", "PerEvent")
 */
export function itemUnitEnumToString(enumValue: ItemUnitEnum): string {
  const enumName = ItemUnitEnum[enumValue] // e.g., "ITEM_UNIT_PER_HOUR"
  return enumName
    .replace('ITEM_UNIT_', '') // e.g., "PER_HOUR"
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('') // e.g., "PerHour"
}

/**
 * Convert backend string to ItemUnitEnum
 * Example: "PerHour" → ItemUnitEnum.ITEM_UNIT_PER_HOUR (2)
 *
 * @param unitString - Backend string format (e.g., "PerHour", "PerEvent")
 * @returns The corresponding ItemUnitEnum value
 */
export function stringToItemUnitEnum(unitString: string): ItemUnitEnum {
  // Convert "PerHour" to "PER_HOUR"
  const snakeCaseUnit = unitString.replace(/([A-Z])/g, '_$1').toUpperCase().slice(1)
  const enumKey = `ITEM_UNIT_${snakeCaseUnit}` as keyof typeof ItemUnitEnum
  return ItemUnitEnum[enumKey] || ItemUnitEnum.ITEM_UNIT_PER_EVENT
}

/**
 * Convert backend inquiry-stage string to InquiryStageEnum
 * Example: "QuoteSent" → InquiryStageEnum.INQUIRY_STAGE_QUOTE_SENT
 *
 * The agent's `render_data` tool passes through Go's PascalCase stage names
 * (e.g. "New", "InProgress", "QuoteSent") rather than the proto enum's
 * SCREAMING_SNAKE_CASE names. Unrecognized names map to UNKNOWN rather than
 * guessing at a default stage.
 *
 * @param name - PascalCase stage name from the backend (e.g. "QuoteSent")
 * @returns The corresponding InquiryStageEnum value, or UNKNOWN if unrecognized
 */
export function inquiryStageFromName(name: string): InquiryStageEnum {
  const snakeCaseName = name.replace(/([A-Z])/g, '_$1').toUpperCase().slice(1)
  const enumKey = `INQUIRY_STAGE_${snakeCaseName}` as keyof typeof InquiryStageEnum
  return InquiryStageEnum[enumKey] ?? InquiryStageEnum.INQUIRY_STAGE_UNKNOWN
}

/**
 * Convert backend event-stage string to EventStageEnum
 * Example: "Upcoming" → EventStageEnum.EVENT_STAGE_UPCOMING
 *
 * Same PascalCase-from-Go situation as {@link inquiryStageFromName}. Unrecognized
 * names map to UNKNOWN rather than guessing at a default stage.
 *
 * @param name - PascalCase stage name from the backend (e.g. "Rescheduled")
 * @returns The corresponding EventStageEnum value, or UNKNOWN if unrecognized
 */
export function eventStageFromName(name: string): EventStageEnum {
  const snakeCaseName = name.replace(/([A-Z])/g, '_$1').toUpperCase().slice(1)
  const enumKey = `EVENT_STAGE_${snakeCaseName}` as keyof typeof EventStageEnum
  return EventStageEnum[enumKey] ?? EventStageEnum.EVENT_STAGE_UNKNOWN
}

/**
 * Generic function to get color for any enum type
 * Useful when you have a union type or unknown enum
 */
export function getEnumColor(enumValue: number, enumType: string): BadgeVariant {
  switch (enumType) {
    case 'InquiryStage':
      return getInquiryStageColor(enumValue)
    case 'InquiryTag':
      return getInquiryTagColor(enumValue)
    case 'BookingStage':
      return getBookingStageColor(enumValue)
    case 'EventStage':
      return getEventStageColor(enumValue)
    case 'QuoteStatus':
      return getQuoteStatusColor(enumValue)
    case 'PaymentStatus':
      return getPaymentStatusColor(enumValue)
    case 'ItemUnit':
      return getItemUnitColor(enumValue)
    case 'ParentType':
      return getParentTypeColor(enumValue)
    case 'DiscountType':
      return getDiscountTypeColor(enumValue)
    case 'UserSchedulingStatus':
      return getUserSchedulingStatusColor(enumValue)
    case 'TaskStatus':
      return getTaskStatusColor(enumValue)
    case 'OrganizationRole':
      return getOrganizationRoleColor(enumValue)
    case 'OrganizationFeature':
      return getOrganizationFeatureColor(enumValue)
    case 'Permission':
      return getPermissionColor(enumValue)
    case 'EmailThreadStatus':
      return getEmailThreadStatusColor(enumValue)
    default:
      return 'default'
  }
}

/**
 * Get both label and color for an enum value
 * Useful for rendering badges or status indicators
 */
export function getEnumDisplay(enumValue: number, enumType: string) {
  // This assumes the enum value name can be retrieved
  // You may need to pass the actual enum name from your settings
  return {
    color: getEnumColor(enumValue, enumType),
  }
}
